import { useState } from 'react';
import type { OptionId } from '../../core/model/brands.ts';
import type { CanonicalQuestion, Option } from '../../core/model/canonical.ts';
import { optionsOf } from '../../core/model/canonical.ts';
import type { QuestionPatch } from '../../core/export/reviewedExam.ts';
import { useI18n } from '../../i18n/index.tsx';
import { Button } from '../../ui/primitives.tsx';

/**
 * Fixing a question you have just found to be wrong.
 *
 * This is the other end of "report a problem". Reporting records that something is wrong; this
 * records what it should say instead, and the two together produce a corrected exam file the
 * author can actually use.
 *
 * Deliberately narrow. It edits the four things a candidate is in a position to be right about
 * after taking the exam — which answer is correct, what the explanation should say, what an option
 * should read, and what a missing image showed — and nothing else. Adding questions, changing types
 * or rewriting prompts is authoring, and authoring belongs in an editor, not in a results screen.
 */
const REASONS = [
  'incorrect-answer',
  'incorrect-explanation',
  'typo',
  'media-description',
  'ambiguous-question',
  'outdated-content',
  'conversion-error',
  'translation-error',
  'other',
] as const;

export function CorrectQuestion({
  question,
  patch,
  onChange,
}: {
  readonly question: CanonicalQuestion;
  readonly patch: QuestionPatch | undefined;
  readonly onChange: (patch: QuestionPatch | undefined) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(patch !== undefined);

  const current: QuestionPatch = patch ?? { questionId: question.id, reason: 'incorrect-answer' };
  const edit = (change: Partial<QuestionPatch>): void => {
    onChange({ ...current, ...change });
  };

  if (!open) {
    return (
      <div className="correct">
        <Button
          variant="quiet"
          onClick={() => {
            setOpen(true);
          }}
        >
          {t('correct.open')}
        </Button>
      </div>
    );
  }

  const choices = question.type === 'single-choice' || question.type === 'multiple-choice';
  const describable = question.content.media.filter(
    (media) => media.role !== 'decorative' && media.availability === 'missing',
  );

  return (
    <section className="correct correct--open" aria-label={t('correct.open')}>
      <h3>{t('correct.title')}</h3>
      <p className="field__help">{t('correct.help')}</p>

      <label className="field">
        <span>{t('correct.reason')}</span>
        <select
          value={current.reason}
          onChange={(event) => {
            edit({ reason: event.target.value as QuestionPatch['reason'] });
          }}
        >
          {REASONS.map((reason) => (
            <option key={reason} value={reason}>
              {t(`correct.reason.${reason}` as const)}
            </option>
          ))}
        </select>
      </label>

      {choices && (
        <fieldset className="checkbox-group">
          <legend>{t('correct.answer')}</legend>
          {question.interaction.options.map((option) => (
            <AnswerChoice
              key={option.id}
              option={option}
              multiple={question.type === 'multiple-choice'}
              checked={isChosen(question, current, option.id)}
              onPick={() => {
                edit(pickAnswer(question, current, option.id));
              }}
            />
          ))}
        </fieldset>
      )}

      <fieldset className="checkbox-group">
        <legend>{t('correct.optionText')}</legend>
        {optionsOf(question).map((option) => (
          <label key={option.id} className="field">
            <span className="visually-hidden">{option.text}</span>
            <input
              type="text"
              defaultValue={current.optionText?.[option.id] ?? option.text}
              onChange={(event) => {
                const text = event.target.value;
                const others = Object.entries(current.optionText ?? {}).filter(
                  ([id]) => id !== option.id,
                );
                // Typed back to what it already said: that is not a correction, so it drops out.
                edit({
                  optionText: Object.fromEntries(
                    text === option.text ? others : [...others, [option.id, text]],
                  ),
                });
              }}
            />
          </label>
        ))}
      </fieldset>

      <label className="field">
        <span>{t('correct.explanation')}</span>
        <textarea
          rows={3}
          defaultValue={current.explanationSummary ?? question.explanation?.summary ?? ''}
          onChange={(event) => {
            edit({ explanationSummary: event.target.value });
          }}
        />
      </label>

      {describable.map((media) => (
        <label key={media.id} className="field">
          <span>{t('correct.mediaDescription', { alt: media.alt ?? media.id })}</span>
          <textarea
            rows={2}
            defaultValue={current.mediaDescription?.[media.id] ?? media.description ?? ''}
            onChange={(event) => {
              edit({ mediaDescription: { ...current.mediaDescription, [media.id]: event.target.value } });
            }}
          />
        </label>
      ))}

      <label className="field">
        <span>{t('correct.note')}</span>
        <input
          type="text"
          defaultValue={current.note ?? ''}
          onChange={(event) => {
            edit({ note: event.target.value });
          }}
        />
      </label>

      <div className="actions">
        <Button
          variant="quiet"
          onClick={() => {
            onChange(undefined);
            setOpen(false);
          }}
        >
          {t('correct.discard')}
        </Button>
      </div>
    </section>
  );
}

function AnswerChoice({
  option,
  multiple,
  checked,
  onPick,
}: {
  readonly option: Option;
  readonly multiple: boolean;
  readonly checked: boolean;
  readonly onPick: () => void;
}) {
  return (
    <label className="checkbox">
      <input type={multiple ? 'checkbox' : 'radio'} checked={checked} onChange={onPick} />
      <span>{option.text}</span>
    </label>
  );
}

/** Whether this option is currently marked correct, taking the patch into account. */
function isChosen(question: CanonicalQuestion, patch: QuestionPatch, id: OptionId): boolean {
  if (question.type === 'single-choice') {
    return (patch.correctOptionId ?? question.interaction.correctOptionId) === id;
  }
  if (question.type === 'multiple-choice') {
    return (patch.correctOptionIds ?? question.interaction.correctOptionIds).includes(id);
  }
  return false;
}

/** The patch that picking this option produces: one answer, or one added to or removed from a set. */
function pickAnswer(
  question: CanonicalQuestion,
  patch: QuestionPatch,
  id: OptionId,
): Partial<QuestionPatch> {
  if (question.type === 'single-choice') {
    return { correctOptionId: id };
  }
  if (question.type !== 'multiple-choice') {
    return {};
  }

  const current = patch.correctOptionIds ?? question.interaction.correctOptionIds;
  return {
    correctOptionIds: current.includes(id)
      ? current.filter((entry) => entry !== id)
      : [...current, id],
  };
}
